import type { MediaAvailability } from '@mimir/common/dto/mediaAvailability';
import type { ItemMediaBase} from '@mimir/common/dto/mediaDtos';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { Fraction } from '@mimir/common/util/fractionType';
import { approximateFractionValue } from '@mimir/common/util/integerMath';
import { TaskTracking } from '@mimir/common/async/taskTracking';
import type { BufferingContext, FragmentedMp4Provider } from './fetching';
import { BufferFetching } from './fetching';
import { StreamingMediaSource } from './mediaSourceExtension';
import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import type { SegmentUrlResolver } from '@mimir/client-common/util/segmentUrlCache';
import type { BufferingParameters } from './bufferingTasks';
import { CmafSegmentFetcher } from './cmafSegmentFetcher';
import { allExceptionDetails } from '@mimir/common/util/exception';
import type { NextTask } from './tasks';
import { ObservableState } from '@mimir/common/util/observable';
import type { AudioTrackConfig, AudioTrackConfigUpdate } from '../model/audioTrackConfig';
import { updateAudioTrackConfig } from '../model/audioTrackConfig';
import type { Cleanable } from '@mimir/common/util/cleanable';
import { FlacDownmixTSRemuxer } from './flacDownmixTSRemuxer';

export interface ItemMediaProvider extends ItemMediaBase {
  segmentResolver: SegmentUrlResolver;

  /**
  * The stream set to play
  */
  streamSet: StreamRoleId[],

  playableDuration: Fraction,
}


/**
 * Combines all the different update sources that trigger and is used to derive
 * what and when to buffer media segments.
 *
 * To allow it to work nicely with Vue and potentially other web frameworks,
 * this class does not lay claim to the <video> element.
 *
 * Instead, it only needs a reference to the video element to be able to grab
 * its currentTime in order to figure out what media to buffer.
 *
 * Things that users of this class will need to provide:
 *  * Where to store the media source URL. (constructor)
 *  * The base URL to fetch media from. (setBaseUrl)
 *  * The availability of media. (setAvailability)
 *  * The media element to obtain currentTime from. (setMediaElement)
 *
 * The setBaseUrl(), setAvailability() and setMediaElement() members can be
 * called in any order and at any time.
 *
 * Things that users of this class will need to do:
 *  * Apply the media source URL to the video element.
 *  * Call bufferMore() on video element events 'timeupdate', 'seeking', and
 *    'loadedmetadata'.
 */
export class ActiveBuffering {
  private streamingMediaSource: StreamingMediaSource;
  private fragmentedMp4Provider: FragmentedMp4Provider;
  private tracking: TaskTracking<unknown>;
  private closed: boolean = false;
  private cleanables: Cleanable[] = [];
  private audioTrackCount = new ObservableState<number | null>(null);

  constructor(
    private itemMediaProvider: ItemMediaProvider,
    private mediaElement: Pick<HTMLMediaElement, 'currentTime' | 'src'>,
    private bufferingBlocker: ObservableState<string | null>,
    private bufferingTracking: ObservableState<string | null>,
    private bufferingDebug: ObservableState<string | null>,
    private audioTrackConfigs: ObservableState<AudioTrackConfig[]>,
    private playerId: string,
    defaultLeftChannels: Set<number>,
    defaultRightChannels: Set<number>,
  ) {
    // When the audio track count changes, we want to initialize the audio track config
    this.cleanables.push(this.audioTrackCount.observe(n => {
      const config: AudioTrackConfig[] = [];
      const hasDefaultConfig = defaultLeftChannels.size > 0 || defaultRightChannels.size > 0;
      for (let i = 0; i < (n || 0); i++) {
        const shouldPlay = !hasDefaultConfig || defaultLeftChannels.has(i) || defaultRightChannels.has(i);
        config.push({
          isMuted: !shouldPlay,
        });
      }
      audioTrackConfigs.value = config;
    }));

    // Recheck buffering state every time we complete a buffering task.
    this.tracking = new TaskTracking((concurrency, cause) => {
      (async () => {
        if (cause) {
          // Don't start more concurrently, if we're currently failing.
          if (concurrency) {
            return;
          }

          // Wait a bit between failures, to avoid spamming.
          await asyncTimeout(1000);
        }
        await this.bufferMore();
      })().catch(ex => {
        if (!this.closed) {
          console.error(`${playerId}: ActiveBuffering buffer more after completed task`, allExceptionDetails(ex));
        }
      });
    });

    this.streamingMediaSource =
      new StreamingMediaSource(
        itemMediaProvider.media.codecsByStreamId,
        itemMediaProvider.streamSet,
        () => {
          this.updateMediaAvailabilityAndDuration(itemMediaProvider.media, itemMediaProvider.playableDuration);
          this.startBufferMore('sourceopen');
        },
      );

    const fetching = new BufferFetching();

    this.fragmentedMp4Provider = this.makeFMp4Provider(itemMediaProvider, fetching);
    this.cleanables.push(this.fragmentedMp4Provider);

    // When the audio track configs change, we want to update the streaming media source
    // so it can adjust the downmixing
    this.cleanables.push(this.audioTrackConfigs.observe(v => {
      this.updateAudioTransform(v, defaultLeftChannels, defaultRightChannels);
    }));
  }

  /**
   * Given the current audio track config and the default webproxy downmix matrix,
   * this function updates the audio transform used by the media source extension.
   * If the audio transform corresponds to a different mixing that previously, the
   * media source extension will evict cached data and re-buffer using the new
   * transform to adjust channels.
   */
  private updateAudioTransform(
    audioTrackConfig: AudioTrackConfig[],
    defaultLeftChannels: Set<number>,
    defaultRightChannels: Set<number>,
  ) {
    const mixMatrix: number[][] = [];
    const allChannelsInDownmix = [...Array.from(defaultLeftChannels), ...Array.from(defaultRightChannels)];
    const maxChannelInDownmix = allChannelsInDownmix.length === 0 ? -1 : Math.max(...allChannelsInDownmix);

    if (this.audioTrackCount.value === null) {
      // If we are using Flac downmix but we don't know yet how many channels there are,
      // let's initialize the mix matrix with the default webproxy downmix. If there is no
      // default webproxy downmix, we pass an empty array that will be treated by the transmuxer
      // as the default layout (even channels to left, odd channels to right)
      for (let i = 0; i <= maxChannelInDownmix; i++) {
        mixMatrix.push([
          defaultLeftChannels.has(i) ? 1.0 : 0.0,
          defaultRightChannels.has(i) ? 1.0 : 0.0,
        ]);
      }

      void this.streamingMediaSource.setAudioTransform({
        type: 'flacDownmix',
        mixMatrix,
      });
      return;
    }

    // If we are using flacDownmix and we know how many channels there are, we calculate the
    // mix matrix by checking which tracks should be played and if they should, whether or not
    // they are part of the default downmix so that we can use the proper channels configured
    // in the downmix
    const tracksToPlay = new Set<number>();
    for (let i = 0; i < audioTrackConfig.length; i++) {
      const track = audioTrackConfig[i];
      if (!track.isMuted) {
        tracksToPlay.add(i);
      }
    }

    for (let i = 0; i < audioTrackConfig.length; i++) {
      let playOnLeft: boolean;
      let playOnRight: boolean;
      if (defaultLeftChannels.has(i) || defaultRightChannels.has(i)) {
        // If the channel is mentionned in the downmix, let's use the downmix info for it
        playOnLeft = defaultLeftChannels.has(i);
        playOnRight = defaultRightChannels.has(i);
      } else {
        playOnLeft = (i % 2) === 0;
        playOnRight = !playOnLeft;
      }
      mixMatrix.push([
        playOnLeft && tracksToPlay.has(i) ? 1.0 : 0.0,
        playOnRight && tracksToPlay.has(i) ? 1.0 : 0.0,
      ]);
    }

    // It could happen that the webproxy downmix configures more channels than there actually
    // are in a given clip (for instance, if we ingest a stereo video clip in a Kelda live folder
    // configured for 0,2,4,6 to left and 1,3,5,7 to right). In such a case, it is important to
    // preserve the extra downmix information because it will allow this matrix to be the same as
    // the default one constructed the first time (i.e. before we knew the number of channels)
    // purely from the default downmix. The reason it is important is that this will avoid unnecessary
    // cache eviction
    for (let i = audioTrackConfig.length; i <= maxChannelInDownmix; i++) {
      mixMatrix.push([
        defaultLeftChannels.has(i) ? 1.0 : 0.0,
        defaultRightChannels.has(i) ? 1.0 : 0.0,
      ]);
    }

    void this.streamingMediaSource.setAudioTransform({
      type: 'flacDownmix',
      mixMatrix,
    });
  }

  public updateMediaAvailabilityAndDuration(media: MediaAvailability, duration: Fraction) {
    const seekableEnd = approximateFractionValue(duration);
    this.itemMediaProvider.media = media;
    this.streamingMediaSource.expandSeekableEnd(seekableEnd);
    this.startBufferMore('updateMediaAvailabilityAndDuration');
  }

  /**
   * Returns the URL of the Media Source, if a Media Source has been created.
   * Note that a call to both setItemMedia() and setMediaElement() is needed
   * before this function stops returning null.
   */
  public getMediaUrl(): string {
    return this.streamingMediaSource.getUrl();
  }


  private makeFMp4Provider(
    itemMedia: ItemMediaProvider,
    fetching: BufferFetching,
  ): FragmentedMp4Provider {
    const segmentResolver = itemMedia.segmentResolver;

    const codecs = itemMedia.media?.codecsByStreamId;
    const isAudioTs = codecs[StreamRoleId.LiveRecordAudioOnlyHighresTs] !== undefined || codecs[StreamRoleId.LiveRecordAudioOnlyProxyTs] !== undefined;
    const isMpegTs = codecs && (codecs[StreamRoleId.LiveRecordWebproxyTs] || codecs[StreamRoleId.LiveRecordHighresTs] || isAudioTs);
    if (isMpegTs) {
      return new FlacDownmixTSRemuxer(segmentResolver, fetching, itemMedia.id, this.audioTrackCount, this.playerId, isAudioTs);
    }

    return new CmafSegmentFetcher(segmentResolver, fetching, itemMedia.id);
  }

  public startBufferMore(initiatorDescription: string): void {
    this.bufferMore().catch((error) => {
      if (!this.closed) {
        console.error(`${this.playerId}: bufferMore task initiated by ${initiatorDescription} failed: ${error}`);
      }
    });
  }

  /**
   * Perform buffering of as many media segments that the current execution
   * state allows. After any of the update sources change, either through a call
   * to one of the set...() operations or through the completion of previous
   * asynchronous operations, there is a chance that performing another
   * bufferMore() call will buffer additional media segments.
   */
  private async bufferMore(): Promise<void> {
    const running: Promise<unknown>[] = [];
    while (true) {
      const nextTask = this.nextBufferTask();
      if (nextTask.task == null) {
        this.bufferingBlocker.value = nextTask.blocker;
        break;
      }


      if (this.tracking.isOngoing(nextTask.id)) {
        console.warn('Tried to buffer ' + nextTask.id + ' more than once');
        break;
      }
      /*
        Start the found buffering task, so that the execution state changes
        before evaluating what the next buffer task should be.
      */
      running.push(this.tracking.runOrPiggyback(nextTask.id, nextTask.task));
    }

    this.bufferingTracking.value = this.tracking.toString();
    await Promise.all(running);
    this.bufferingTracking.value = this.tracking.toString();
    const debug = this.streamingMediaSource.debug;
    if (debug) {
      this.bufferingDebug.value = debug;
    }
  }

  /**
   * Return the next buffering task, or a description of what is blocking the
   * next buffering task from happening.
   *
   * It is safe to not execute a returned buffering task, but if executed, it
   * should be done so immediately to avoid the buffering task from acting on
   * stale information.
   */
  nextBufferTask(): NextTask {
    if (this.streamingMediaSource.getUrl() != this.mediaElement.src) {
      return {
        blocker: 'Media element currently assigned to a different media source (current=' + this.mediaElement.src + ', mse=' + this.streamingMediaSource.getUrl() + ')'
      };
    }

    if (this.itemMediaProvider.id != this.fragmentedMp4Provider.itemId) {
      return {
        blocker: 'Availability does not match source'
      };
    }

    const concurrency = this.tracking.concurrency;
    if (concurrency > 5) {
      return {
        blocker: concurrency + ' fetches in progress'
      };
    }

    const media = this.itemMediaProvider.media;
    const currentTime = this.mediaElement.currentTime;
    const segmentDuration = media.segmentDuration;

    const context: BufferingContext = {
      fragmentedMp4Provider: this.fragmentedMp4Provider,
      currentTime
    };

    const params: BufferingParameters = {
      prebufferDuration: (concurrency > 2) ? approximateFractionValue(media.segmentDuration) : 30.0,
      segmentCount:  media.segmentCount,
      streaming: media.streaming
    };
    return this.streamingMediaSource.nextBufferTask(context, params, segmentDuration);
  }

  close() {
    this.closed = true;
    this.streamingMediaSource.close();
    this.cleanables.forEach(c => c.cleanup());
    // Let's reset to null the audio track count after we have cleaned up the observables,
    // so that it does not trigger anything on the streaming media source
    this.audioTrackCount.value = null;
  }

  updateAudioTrackConfig(update: AudioTrackConfigUpdate[]) {
    this.audioTrackConfigs.value =
      updateAudioTrackConfig(this.audioTrackConfigs.value, update);
  }
}

